import { ART_PAL, fade, mix, n, shade, tint, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Roulette scene behind the game zone: an evening hall in warm lacquer (a sunlit amber room in light). The light of
 * lamps outside the frame pools at both ends of the strip and at the far edges, a soft warm pool lies over the middle.
 * The layout is all panels (timer, history, strip, stake row, columns), so the scene shows through the strip window,
 * the gaps and the dimmed columns only — and the strip cells and the dimmed columns are translucent, so the motifs are
 * light only: an object (a sconce, a counter, a screen of slats) would show through them or run under a panel.
 * Motifs are marked `data-motif="<x>"` (tests keep them out of the middle 40%). No filters, no text, no wheel, no chips.
 */
export function rouletteScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `sr${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;

  const top = P.dark ? mix(P.bg, P.orange, 0.1) : mix(P.gold, '#FFFFFF', 0.84);
  const mid = P.dark ? mix(P.bg, P.red, 0.08) : mix(P.orange, '#FFFFFF', 0.9);
  const low = P.dark ? mix(P.bg, P.orange, 0.05) : mix(P.red, '#FFFFFF', 0.9);
  const globe = P.dark ? '#FFE6B0' : '#FFF4DA';

  const defs =
    `<defs>${vgrad(`${p}bg`, top, mid, low)}` +
    fade(`${p}lh`, P.dark ? P.gold : P.orange, P.dark ? 0.26 : 0.3) +
    fade(`${p}hl`, globe, P.dark ? 0.25 : 0.6) +
    fade(`${p}pool`, P.dark ? P.orange : P.gold, P.dark ? 0.1 : 0.16) +
    fade(`${p}calm`, P.dark ? P.bg : '#FFFFFF', P.dark ? 0.3 : 0.45) +
    `</defs>`;

  /** the light of a lamp outside the frame: a warm pool with a brighter heart (no object: cells and dimmed columns show through) */
  const glow = (x: number, y: number, r: number) =>
    `<g data-motif="${Math.round(x)}"><circle cx="${x}" cy="${y}" r="${r}" fill="url(#${p}lh)"/>` +
    `<circle cx="${x}" cy="${y}" r="${n(r * 0.35)}" fill="url(#${p}hl)"/></g>`;

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}bg)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.3)}" rx="${n(W * 0.45)}" ry="${n(H * 0.4)}" fill="url(#${p}pool)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.45)}" rx="${n(W * 0.32)}" ry="${n(H * 0.42)}" fill="url(#${p}calm)"/>`;

  // at 1440 × 900 the strip window is about y 140…360 of the scene, its ends (x < 230, x > 1370) under the soft fades;
  // nothing lower: a pool behind a dimmed (translucent) column reads as a smudge on it
  if (wide) art += glow(110, 250, 190) + glow(1490, 250, 190);
  // phone: the strip window is about y 115…235; the pools there are plain (no brighter heart)
  else
    art +=
      `<circle data-motif="0" cy="175" r="80" fill="url(#${p}lh)"/>` +
      `<circle data-motif="${W}" cx="${W}" cy="175" r="80" fill="url(#${p}lh)"/>`;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Roulette: a tiny strip of cells — red, slate, red, slate, jade (repeat-x tile, 40 × 8). */
export function rouletteAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const red = P.dark ? P.red : tint(P.red, 0.05);
  const slate = P.dark ? tint(P.slate, 0.25) : P.slate;
  const jade = P.dark ? shade(P.teal, 0.1) : P.teal;
  const cell = (x: number, c: string) =>
    `<rect x="${x + 1}" y="1.5" width="6" height="5" rx="1" fill="${c}"/>`;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 8" width="40" height="8">` +
    cell(0, red) +
    cell(8, slate) +
    cell(16, red) +
    cell(24, slate) +
    cell(32, jade) +
    `</svg>`
  );
}

export default defineScene({ game: 'roulette', scene: rouletteScene, accent: rouletteAccent });
